import { useEffect, useMemo, useState } from "react";
import { HomeOSAvatar } from "./HomeOSAvatar";
import {
  getScreensaverUpcomingItems,
  getHouseholdMoodPhrase,
  getFamilyPhotoCarouselItems,
} from "../utils/homeOsSelectors";

/* ════════════════════════════════════════════════════════════════════════
   WALL SCREENSAVER — calm passive overlay for the Family Wall Display.
   - Giant clock + date
   - Household name
   - Rotating featured family member (8s)
   - Avatar strip
   - Next 3 upcoming events ribbon
   - Mood pulse line
   - Tap / click / key / escape to wake
   ════════════════════════════════════════════════════════════════════════ */

export function WallScreensaver({ open, state, onExit }) {
  const [now, setNow] = useState(new Date());
  const [featuredIndex, setFeaturedIndex] = useState(0);

  /* Clock tick — schedule first at the next minute boundary, then every 60s */
  useEffect(() => {
    if (!open) return undefined;
    setNow(new Date());
    const msToNextMinute = 60_000 - (new Date().getSeconds() * 1000 + new Date().getMilliseconds());
    let interval;
    const align = setTimeout(() => {
      setNow(new Date());
      interval = setInterval(() => setNow(new Date()), 60_000);
    }, msToNextMinute);
    return () => { clearTimeout(align); if (interval) clearInterval(interval); };
  }, [open]);

  const carousel = useMemo(() => getFamilyPhotoCarouselItems(state || {}), [state]);
  const upcoming = useMemo(() => getScreensaverUpcomingItems(state || {}, 3), [state]);
  const moodPhrase = useMemo(() => getHouseholdMoodPhrase(state || {}), [state]);

  /* Featured rotation — every 8s while open */
  useEffect(() => {
    if (!open || carousel.length === 0) return undefined;
    const t = setInterval(() => setFeaturedIndex(i => (i + 1) % carousel.length), 8_000);
    return () => clearInterval(t);
  }, [open, carousel.length]);

  /* Keyboard: Escape exits */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onExit(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onExit]);

  if (!open) return null;

  const featured = carousel[featuredIndex] || null;
  const timeLabel = now.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit", hour12: false });
  const dateLabel = now.toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const householdName = state?.settings?.householdName || "Our Home";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onExit}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onExit(); }}
      className="fixed inset-0 z-[60] flex items-center justify-center cursor-pointer font-home animate-fadeIn"
      data-testid="wall-screensaver"
      aria-label="Screensaver — tap to wake"
    >
      {/* Background — soft animated gradient, blurred backdrop */}
      <div className="absolute inset-0  from-slate-900  " />
      <div className="absolute inset-0 backdrop-blur-2xl bg-black/30" />
      <div className="absolute -top-32 -right-20 w-[480px] h-[480px] rounded-full bg-transparent blur-3xl" aria-hidden />
      <div className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full bg-transparent blur-3xl" aria-hidden />
      <div className="absolute top-1/3 left-1/4 w-[320px] h-[320px] rounded-full bg-amber-400/10 blur-3xl" aria-hidden />

      <div className="relative w-full max-w-5xl px-6 sm:px-10 py-10 flex flex-col items-center text-center text-white">

        {/* Household name */}
        <p className="text-[11px] sm:text-sm font-extrabold uppercase tracking-[0.4em] text-white/70" data-testid="screensaver-household">
          {householdName}
        </p>

        {/* Giant clock */}
        <h1
          className="mt-3 text-[7rem] sm:text-[10rem] lg:text-[13rem] font-extrabold tabular-nums leading-none tracking-tight drop-shadow-none"
          data-testid="screensaver-clock"
          aria-label={`Current time ${timeLabel}`}
        >
          {timeLabel}
        </h1>
        <p className="mt-1 text-lg sm:text-2xl font-bold text-white/85" data-testid="screensaver-date">{dateLabel}</p>

        {/* Featured family member */}
        {featured && (
          <div className="mt-8 sm:mt-10 flex flex-col items-center" data-testid="screensaver-featured">
            <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-[28px] overflow-hidden border-4 border-white/30 shadow-none flex items-center justify-center"
                 style={featured.avatarUrl ? undefined : { backgroundColor: featured.color }}>
              {featured.avatarUrl ? (
                <img src={featured.avatarUrl} alt={featured.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-5xl sm:text-7xl font-extrabold text-white">{featured.name?.[0] || "·"}</span>
              )}
            </div>
            <p className="mt-3 text-xl sm:text-2xl font-extrabold">{featured.name}</p>
            {featured.role && <p className="text-sm font-bold text-white/70">{featured.role}</p>}
          </div>
        )}

        {/* Avatar strip */}
        {carousel.length > 1 && (
          <div className="mt-6 flex items-center gap-2 flex-wrap justify-center" data-testid="screensaver-avatars">
            {carousel.map((m, idx) => (
              <button
                key={m.id}
                type="button"
                onClick={(e) => { e.stopPropagation(); setFeaturedIndex(idx); }}
                className={`w-10 h-10 rounded-full overflow-hidden border-2 transition-all ${idx === featuredIndex ? "border-white scale-110" : "border-white/30"}`}
                style={m.avatarUrl ? undefined : { backgroundColor: m.color }}
                aria-label={`Feature ${m.name}`}
                data-testid={`screensaver-avatar-${m.id}`}
              >
                {m.avatarUrl
                  ? <img src={m.avatarUrl} alt={m.name} className="w-full h-full object-cover" />
                  : <span className="text-white font-extrabold text-sm flex items-center justify-center w-full h-full">{m.name?.[0] || "·"}</span>}
              </button>
            ))}
          </div>
        )}

        {/* Mood pulse */}
        <div className="mt-8 px-5 py-2.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm" data-testid="screensaver-mood">
          <p className="text-base sm:text-lg font-bold text-white/90">
            {moodPhrase.checkedIn > 0
              ? `${moodPhrase.checkedIn} of ${moodPhrase.total} checked in · ${moodPhrase.phrase}`
              : moodPhrase.phrase}
          </p>
        </div>

        {/* Upcoming events ribbon */}
        {upcoming.length > 0 && (
          <div className="mt-6 w-full max-w-3xl" data-testid="screensaver-upcoming">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-white/60 mb-2">Coming up</p>
            <ul className="flex flex-col sm:flex-row gap-2 justify-center">
              {upcoming.map(item => {
                const mod = item.module;
                return (
                  <li
                    key={item.id}
                    className="flex-1 rounded-2xl bg-white/10 border border-white/15 px-4 py-3 text-left backdrop-blur-sm"
                    data-testid={`screensaver-upcoming-${item.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${mod.color.dot}`} aria-hidden />
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-white/70">{mod.label}</span>
                      {item.dueTime && <span className="ml-auto text-xs font-extrabold text-white/80 tabular-nums">{item.dueTime}</span>}
                    </div>
                    <p className="mt-1 text-base font-extrabold text-white truncate">{item.title}</p>
                    <p className="text-[11px] text-white/60">{item.dueDate}</p>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Wake hint */}
        <p className="mt-10 text-sm font-bold text-white/50 animate-pulse" data-testid="screensaver-hint">
          Tap anywhere to return to Family Wall
        </p>
      </div>
    </div>
  );
}

export default WallScreensaver;
